import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { device } from "../lib/breakpoints";
import Button from "../components/Button";
import { trackEvent } from "../helpers/mixpanel";
import SEO from "../components/SEO";
import { APP_NAME } from "../helpers/constants";
import toast from "react-hot-toast";

const BookingSettings = () => {
  const [activeTab, setActiveTab] = useState("services");
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({});
  const [bookingSettings, setBookingSettings] = useState({});
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    name: "",
    description: "",
    duration: 60,
    price: 0,
    category: "leasing",
    prepTime: 15,
    active: true,
  });

  useEffect(() => {
    // Mock services data
    setServices([
      {
        id: 1,
        name: "Property tour (30 min)",
        description: "Guided walkthrough for prospective residents",
        duration: 30,
        price: 0,
        category: "leasing",
        prepTime: 15,
        active: true,
      },
      {
        id: 2,
        name: "Move-in / move-out inspection",
        description: "Document unit condition and capture photos with the resident",
        duration: 60,
        price: 0,
        category: "inspections",
        prepTime: 15,
        active: true,
      },
      {
        id: 3,
        name: "Lease signing (virtual)",
        description: "Scheduled time to review and sign the lease package",
        duration: 45,
        price: 0,
        category: "leasing",
        prepTime: 15,
        active: true,
      },
      {
        id: 4,
        name: "Key handoff / lockbox",
        description: "Coordinate key pickup or lockbox access",
        duration: 20,
        price: 0,
        category: "ops",
        prepTime: 10,
        active: true,
      },
      {
        id: 5,
        name: "Owner / vendor call",
        description: "Time blocked for a maintenance or vendor discussion",
        duration: 30,
        price: 0,
        category: "other",
        prepTime: 10,
        active: true,
      },
    ]);

    // Mock availability data
    setAvailability({
      monday: {
        enabled: true,
        start: "09:00",
        end: "17:00",
        break: { start: "12:00", end: "13:00" },
      },
      tuesday: {
        enabled: true,
        start: "09:00",
        end: "17:00",
        break: { start: "12:00", end: "13:00" },
      },
      wednesday: {
        enabled: true,
        start: "09:00",
        end: "17:00",
        break: { start: "12:00", end: "13:00" },
      },
      thursday: {
        enabled: true,
        start: "09:00",
        end: "19:00",
        break: { start: "12:00", end: "13:00" },
      },
      friday: {
        enabled: true,
        start: "09:00",
        end: "19:00",
        break: { start: "12:00", end: "13:00" },
      },
      saturday: { enabled: true, start: "10:00", end: "16:00", break: null },
      sunday: { enabled: false, start: "10:00", end: "16:00", break: null },
    });

    // Mock booking settings
    setBookingSettings({
      advanceBookingDays: 30,
      cancellationHours: 24,
      requireDeposit: true,
      depositAmount: 50,
      autoConfirm: true,
      bufferTime: 15,
      timeZone: "America/New_York",
    });
  }, []);

  const handleSaveService = () => {
    if (editingService) {
      setServices(
        services.map((s) =>
          s.id === editingService.id
            ? { ...newService, id: editingService.id }
            : s
        )
      );
      toast.success("Service updated successfully!");
      trackEvent("Service Updated", { service_name: newService.name });
    } else {
      const service = { ...newService, id: Date.now() };
      setServices([...services, service]);
      toast.success("Service added successfully!");
      trackEvent("Service Added", { service_name: newService.name });
    }

    setNewService({
      name: "",
      description: "",
      duration: 60,
      price: 0,
      category: "leasing",
      prepTime: 15,
      active: true,
    });
    setEditingService(null);
    setShowAddServiceModal(false);
  };

  const handleEditService = (service) => {
    setNewService(service);
    setEditingService(service);
    setShowAddServiceModal(true);
  };

  const handleDeleteService = (serviceId) => {
    setServices(services.filter((s) => s.id !== serviceId));
    toast.success("Service deleted successfully!");
    trackEvent("Service Deleted", { service_id: serviceId });
  };

  const handleToggleService = (serviceId) => {
    setServices(
      services.map((s) =>
        s.id === serviceId ? { ...s, active: !s.active } : s
      )
    );
  };

  const handleAvailabilityChange = (day, field, value) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleSaveSettings = () => {
    toast.success("Settings saved successfully!");
    trackEvent("Booking Settings Saved");
  };

  const categories = [
    { value: "leasing", label: "Leasing" },
    { value: "inspections", label: "Inspections" },
    { value: "maintenance", label: "Maintenance" },
    { value: "ops", label: "Operations" },
    { value: "other", label: "Other" },
  ];

  const days = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
  ];

  return (
    <Container>
      <SEO
        title={`Booking Settings - ${APP_NAME} | Configure Services & Availability`}
        description={`Configure booking-style services, availability, and rules in ${APP_NAME} (illustration data).`}
        keywords={`booking settings, appointments, service configuration, ${APP_NAME}`}
      />

      {/* Header */}
      <Header>
        <PageTitle>Booking Settings</PageTitle>
        <PageSubtitle>
          Configure your services, availability, and booking preferences
        </PageSubtitle>
      </Header>

      {/* Tab Navigation */}
      <TabNavigation>
        <TabButton
          $active={activeTab === "services"}
          onClick={() => setActiveTab("services")}
        >
          Services & Pricing
        </TabButton>
        <TabButton
          $active={activeTab === "availability"}
          onClick={() => setActiveTab("availability")}
        >
          Availability
        </TabButton>
        <TabButton
          $active={activeTab === "settings"}
          onClick={() => setActiveTab("settings")}
        >
          Booking Rules
        </TabButton>
      </TabNavigation>

      {/* Services Tab */}
      {activeTab === "services" && (
        <TabContent>
          <SectionHeader>
            <SectionTitle>Your Services</SectionTitle>
            <AddButton onClick={() => setShowAddServiceModal(true)}>
              <ButtonIcon>+</ButtonIcon>
              Add Service
            </AddButton>
          </SectionHeader>

          <ServicesGrid>
            {services.map((service) => (
              <ServiceCard key={service.id} $active={service.active}>
                <ServiceHeader>
                  <ServiceInfo>
                    <ServiceName>{service.name}</ServiceName>
                    <ServiceCategory>
                      {
                        categories.find((c) => c.value === service.category)
                          ?.label
                      }
                    </ServiceCategory>
                  </ServiceInfo>
                  <ServiceActions>
                    <ToggleButton
                      onClick={() => handleToggleService(service.id)}
                      $active={service.active}
                    >
                      {service.active ? "Active" : "Inactive"}
                    </ToggleButton>
                  </ServiceActions>
                </ServiceHeader>

                <ServiceDescription>{service.description}</ServiceDescription>

                <ServiceDetails>
                  <DetailItem>
                    <DetailLabel>Duration:</DetailLabel>
                    <DetailValue>{service.duration} min</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Price:</DetailLabel>
                    <DetailValue>${service.price}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Prep Time:</DetailLabel>
                    <DetailValue>{service.prepTime} min</DetailValue>
                  </DetailItem>
                </ServiceDetails>

                <ServiceCardActions>
                  <EditButton onClick={() => handleEditService(service)}>
                    Edit
                  </EditButton>
                  <DeleteButton onClick={() => handleDeleteService(service.id)}>
                    Delete
                  </DeleteButton>
                </ServiceCardActions>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </TabContent>
      )}

      {/* Availability Tab */}
      {activeTab === "availability" && (
        <TabContent>
          <SectionTitle>Weekly Availability</SectionTitle>

          <AvailabilityGrid>
            {days.map((day) => (
              <DayCard key={day.key}>
                <DayHeader>
                  <DayName>{day.label}</DayName>
                  <DayToggle>
                    <input
                      type="checkbox"
                      checked={availability[day.key]?.enabled || false}
                      onChange={(e) =>
                        handleAvailabilityChange(
                          day.key,
                          "enabled",
                          e.target.checked
                        )
                      }
                    />
                    <span>Open</span>
                  </DayToggle>
                </DayHeader>

                {availability[day.key]?.enabled && (
                  <DaySettings>
                    <TimeGroup>
                      <TimeLabel>Operating Hours:</TimeLabel>
                      <TimeInputs>
                        <TimeInput
                          type="time"
                          value={availability[day.key]?.start || "09:00"}
                          onChange={(e) =>
                            handleAvailabilityChange(
                              day.key,
                              "start",
                              e.target.value
                            )
                          }
                        />
                        <span>to</span>
                        <TimeInput
                          type="time"
                          value={availability[day.key]?.end || "17:00"}
                          onChange={(e) =>
                            handleAvailabilityChange(
                              day.key,
                              "end",
                              e.target.value
                            )
                          }
                        />
                      </TimeInputs>
                    </TimeGroup>

                    <BreakToggle>
                      <input
                        type="checkbox"
                        checked={!!availability[day.key]?.break}
                        onChange={(e) =>
                          handleAvailabilityChange(
                            day.key,
                            "break",
                            e.target.checked
                              ? { start: "12:00", end: "13:00" }
                              : null
                          )
                        }
                      />
                      <span>Lunch Break</span>
                    </BreakToggle>

                    {availability[day.key]?.break && (
                      <TimeGroup>
                        <TimeLabel>Break Time:</TimeLabel>
                        <TimeInputs>
                          <TimeInput
                            type="time"
                            value={
                              availability[day.key]?.break?.start || "12:00"
                            }
                            onChange={(e) =>
                              handleAvailabilityChange(day.key, "break", {
                                ...availability[day.key].break,
                                start: e.target.value,
                              })
                            }
                          />
                          <span>to</span>
                          <TimeInput
                            type="time"
                            value={availability[day.key]?.break?.end || "13:00"}
                            onChange={(e) =>
                              handleAvailabilityChange(day.key, "break", {
                                ...availability[day.key].break,
                                end: e.target.value,
                              })
                            }
                          />
                        </TimeInputs>
                      </TimeGroup>
                    )}
                  </DaySettings>
                )}
              </DayCard>
            ))}
          </AvailabilityGrid>
        </TabContent>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <TabContent>
          <SectionTitle>Booking Rules & Preferences</SectionTitle>

          <SettingsGrid>
            <SettingGroup>
              <SettingLabel>Advance Booking</SettingLabel>
              <SettingDescription>
                How far in advance can clients book?
              </SettingDescription>
              <SettingInput>
                <NumberInput
                  type="number"
                  value={bookingSettings.advanceBookingDays}
                  onChange={(e) =>
                    setBookingSettings((prev) => ({
                      ...prev,
                      advanceBookingDays: parseInt(e.target.value),
                    }))
                  }
                />
                <span>days</span>
              </SettingInput>
            </SettingGroup>

            <SettingGroup>
              <SettingLabel>Cancellation Policy</SettingLabel>
              <SettingDescription>
                Minimum notice required for cancellations
              </SettingDescription>
              <SettingInput>
                <NumberInput
                  type="number"
                  value={bookingSettings.cancellationHours}
                  onChange={(e) =>
                    setBookingSettings((prev) => ({
                      ...prev,
                      cancellationHours: parseInt(e.target.value),
                    }))
                  }
                />
                <span>hours</span>
              </SettingInput>
            </SettingGroup>

            <SettingGroup>
              <SettingLabel>Deposit Requirement</SettingLabel>
              <SettingToggle>
                <input
                  type="checkbox"
                  checked={bookingSettings.requireDeposit}
                  onChange={(e) =>
                    setBookingSettings((prev) => ({
                      ...prev,
                      requireDeposit: e.target.checked,
                    }))
                  }
                />
                <span>Require deposit for bookings</span>
              </SettingToggle>
              {bookingSettings.requireDeposit && (
                <SettingInput>
                  <span>$</span>
                  <NumberInput
                    type="number"
                    value={bookingSettings.depositAmount}
                    onChange={(e) =>
                      setBookingSettings((prev) => ({
                        ...prev,
                        depositAmount: parseInt(e.target.value),
                      }))
                    }
                  />
                  <span>deposit amount</span>
                </SettingInput>
              )}
            </SettingGroup>

            <SettingGroup>
              <SettingLabel>Auto-Confirmation</SettingLabel>
              <SettingToggle>
                <input
                  type="checkbox"
                  checked={bookingSettings.autoConfirm}
                  onChange={(e) =>
                    setBookingSettings((prev) => ({
                      ...prev,
                      autoConfirm: e.target.checked,
                    }))
                  }
                />
                <span>Automatically confirm bookings</span>
              </SettingToggle>
            </SettingGroup>

            <SettingGroup>
              <SettingLabel>Buffer Time</SettingLabel>
              <SettingDescription>
                Time between appointments for cleanup/prep
              </SettingDescription>
              <SettingInput>
                <NumberInput
                  type="number"
                  value={bookingSettings.bufferTime}
                  onChange={(e) =>
                    setBookingSettings((prev) => ({
                      ...prev,
                      bufferTime: parseInt(e.target.value),
                    }))
                  }
                />
                <span>minutes</span>
              </SettingInput>
            </SettingGroup>
          </SettingsGrid>
        </TabContent>
      )}

      {/* Save Button */}
      <SaveSection>
        <SaveButton onClick={handleSaveSettings}>Save All Settings</SaveButton>
      </SaveSection>

      {/* Add/Edit Service Modal */}
      {showAddServiceModal && (
        <ModalOverlay onClick={() => setShowAddServiceModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingService ? "Edit Service" : "Add New Service"}
              </ModalTitle>
              <CloseButton onClick={() => setShowAddServiceModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <FormGroup>
                <FormLabel>Service Name *</FormLabel>
                <FormInput
                  type="text"
                  value={newService.name}
                  onChange={(e) =>
                    setNewService((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Property tour"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextArea
                  value={newService.description}
                  onChange={(e) =>
                    setNewService((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Brief description of the service..."
                  rows="3"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FormLabel>Category *</FormLabel>
                  <FormSelect
                    value={newService.category}
                    onChange={(e) =>
                      setNewService((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Duration (minutes) *</FormLabel>
                  <FormInput
                    type="number"
                    value={newService.duration}
                    onChange={(e) =>
                      setNewService((prev) => ({
                        ...prev,
                        duration: parseInt(e.target.value),
                      }))
                    }
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <FormLabel>Price ($) *</FormLabel>
                  <FormInput
                    type="number"
                    value={newService.price}
                    onChange={(e) =>
                      setNewService((prev) => ({
                        ...prev,
                        price: parseInt(e.target.value),
                      }))
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Prep Time (minutes)</FormLabel>
                  <FormInput
                    type="number"
                    value={newService.prepTime}
                    onChange={(e) =>
                      setNewService((prev) => ({
                        ...prev,
                        prepTime: parseInt(e.target.value),
                      }))
                    }
                  />
                </FormGroup>
              </FormRow>
            </ModalBody>

            <ModalFooter>
              <SecondaryButton onClick={() => setShowAddServiceModal(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleSaveService}>
                {editingService ? "Update Service" : "Add Service"}
              </PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default BookingSettings;

// Styled Components
const Container = styled.div`
  width: 100%;
  background: var(--color-bg-primary);
  padding: 2rem;
  min-height: 100vh;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const Header = styled.header`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;

  @media ${device.mobile} {
    font-size: 1.75rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 2px solid var(--color-border-light);
  margin-bottom: 2rem;
  gap: 2rem;

  @media ${device.mobile} {
    gap: 1rem;
    overflow-x: auto;
  }
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  color: ${(props) =>
    props.$active ? "var(--color-primary)" : "var(--color-text-secondary)"};
  border-bottom: 3px solid
    ${(props) => (props.$active ? "var(--color-primary)" : "transparent")};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: var(--color-primary);
  }
`;

const TabContent = styled.div`
  background: var(--color-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-medium);
  padding: 2rem;
  margin-bottom: 2rem;

  @media ${device.mobile} {
    padding: 1.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const AddButton = styled(Button)`
  background: var(--gradient-primary);
  color: var(--color-text-light);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonIcon = styled.span`
  font-size: 1.2rem;
  font-weight: bold;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);
  padding: 1.5rem;
  opacity: ${(props) => (props.$active ? 1 : 0.6)};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: var(--shadow-medium);
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.25rem;
  font-family: "Inter", sans-serif;
`;

const ServiceCategory = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
`;

const ServiceActions = styled.div``;

const ToggleButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: none;
  font-size: 0.8rem;
  font-weight: 500;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;

  ${(props) =>
    props.$active
      ? `
    background: #dcfce7;
    color: #16a34a;
  `
      : `
    background: #fef2f2;
    color: #dc2626;
  `}
`;

const ServiceDescription = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const ServiceDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1rem;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  font-weight: 600;
`;

const ServiceCardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const EditButton = styled.button`
  background: none;
  border: 1px solid var(--color-primary);
  color: var(--color-primary);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-primary);
    color: white;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: 1px solid var(--color-error);
  color: var(--color-error);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-error);
    color: white;
  }
`;

const AvailabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const DayCard = styled.div`
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);
  padding: 1.5rem;
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DayName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const DayToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  cursor: pointer;
`;

const DaySettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TimeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TimeLabel = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const TimeInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TimeInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 6px;
  font-family: "Inter", sans-serif;
`;

const BreakToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  cursor: pointer;
`;

const SettingsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SettingGroup = styled.div`
  padding: 1.5rem;
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
`;

const SettingLabel = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;
`;

const SettingDescription = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin-bottom: 1rem;
`;

const SettingInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SettingToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  cursor: pointer;
  margin-bottom: 1rem;
`;

const NumberInput = styled.input`
  width: 80px;
  padding: 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  text-align: center;
`;

const SaveSection = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
`;

const SaveButton = styled(Button)`
  background: var(--gradient-primary);
  color: var(--color-text-light);
  padding: 1rem 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  box-shadow: var(--shadow-medium);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-dark);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: var(--color-bg-primary);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: var(--shadow-dark);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-border-light);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 8px;
  font-size: 1rem;
  font-family: "Inter", sans-serif;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(60, 141, 188, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 8px;
  font-size: 1rem;
  font-family: "Inter", sans-serif;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(60, 141, 188, 0.1);
  }
`;

const FormTextArea = styled.textarea`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--color-border-medium);
  border-radius: 8px;
  font-size: 1rem;
  font-family: "Inter", sans-serif;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(60, 141, 188, 0.1);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--color-border-light);
`;

const PrimaryButton = styled(Button)`
  background: var(--gradient-primary);
  color: var(--color-text-light);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
`;

const SecondaryButton = styled(Button)`
  background: var(--color-bg-secondary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-medium);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
`;
